import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import _ from 'lodash';
import { DimensionConfigType, KpiConfigType } from 'types/tradePromoOptimization';
interface FetchLenseConfigProp extends HookInputProps {
    setKpiConfigData: Dispatch<SetStateAction<KpiConfigType | null>>;
}
function useFetchKpiConfigData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    setKpiConfigData,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });
    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);
    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && Array.isArray(payload?.data)) {
                const tempObj: any = {};
                //setting all required states
                payload?.data?.forEach((item: any) => {
                    tempObj[item?.value] = {
                        post: item?.post,
                        pre: item?.pre,
                        label: item?.label,
                        allowedDecimals: item?.allowed_decimals,
                        showByDefault: item?.show_by_default,
                        availableFor: item?.available_for || [],
                        planLabel: item?.plan_label,
                    };
                });
                setKpiConfigData(tempObj);
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}
export { useFetchKpiConfigData };
